import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useAuth } from './AuthContext';
import { SubscriptionContext, accessFor } from './SubscriptionContext';
import api from '../services/Api';

export function SubscriptionProvider({ children }) {
    const { user } = useAuth();
    const accountId = user?.playerId;
    const { pathname } = useLocation();
    const roomId = pathname.match(/^\/play\/(\d+)\//)?.[1];
    const [data, setData] = useState(null);
    const [tick, setTick] = useState(Date.now());
    const [ready, setReady] = useState(false);
    const offset = useRef(0);
    const epoch = useRef(0);
    const inFlight = useRef(null);
    const failures = useRef(0);
    const refresh = useCallback(async () => {
        if ((!accountId && !roomId) || document.visibilityState === 'hidden') return;
        if (inFlight.current?.epoch === epoch.current) return inFlight.current.promise;
        const current = epoch.current;
        const promise = (async () => {
            try {
                const result = (await api.get('/api/subscriptions/me', { _roomId: roomId })).data;
                if (current !== epoch.current) return;
                offset.current = Date.parse(result.serverUtcNow) - Date.now();
                failures.current = 0;
                setData(result); setTick(Date.now() + offset.current); setReady(true);
            } catch (_) {
                // A settings outage never logs a player out or ends membership.
                if (current === epoch.current) failures.current = Math.min(failures.current + 1, 3);
            } finally {
                if (inFlight.current?.epoch === current) inFlight.current = null;
            }
        })();
        inFlight.current = { epoch: current, promise };
        return promise;
    }, [accountId, roomId]);
    useEffect(() => {
        epoch.current += 1;
        setData(null); setReady(false); failures.current = 0;
        let disposed = false;
        let timeout;
        const update = async () => {
            clearTimeout(timeout);
            await refresh();
            if (!disposed) timeout = setTimeout(update, Math.min(60000 * (2 ** failures.current), 300000));
        };
        const visible = () => { if (document.visibilityState !== 'hidden') void update(); };
        void update();
        window.addEventListener('pwf-subscription-changed', update);
        window.addEventListener('online', visible);
        document.addEventListener('visibilitychange', visible);
        return () => {
            disposed = true; epoch.current += 1; clearTimeout(timeout);
            window.removeEventListener('pwf-subscription-changed', update);
            window.removeEventListener('online', visible);
            document.removeEventListener('visibilitychange', visible);
        };
    }, [refresh]);
    useEffect(() => {
        // Only access boundaries update the shared room context. A countdown
        // must not rerender the entire card table or voice controls every second.
        const now = Date.now() + offset.current;
        const boundaries = [data?.subscription?.startsAt, data?.subscription?.endsAt]
            .map(value => Date.parse(value)).filter(value => Number.isFinite(value) && value > now);
        if (!boundaries.length) return undefined;
        const timeout = setTimeout(() => setTick(Date.now() + offset.current), Math.min(Math.min(...boundaries) - now + 1, 86400000));
        return () => clearTimeout(timeout);
    }, [data, tick]);
    const value = useMemo(() => ({ data, ready, refresh, now: tick, clockOffset: offset.current,
        active: !!data?.subscription && !data.subscription.cancelled && Date.parse(data.subscription.startsAt) <= tick && Date.parse(data.subscription.endsAt) > tick,
        feature: (game, name) => !ready ? { show: true, enabled: false, allowed: false, locked: false }
            : accessFor(data, game, name, tick) }), [data, ready, refresh, tick]);
    return <SubscriptionContext.Provider value={value}>{children}</SubscriptionContext.Provider>;
}
